import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Save, Info } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";

const VOCI = [
  ["non_mangia", "Non mangia / non beve", "Quando viene segnato in Non mangia o Non beve"],
  ["alvo", "Alvo intestinale", "Quando un ospite non va in bagno da troppi giorni"],
  ["docce", "Doccia rifiutata o non fatta", "Quando la doccia viene segnata come non fatta, con il motivo"],
  ["cadute", "Cadute e infortuni", "Ogni caduta o infortunio registrato"],
  ["parametri", "Parametri critici", "Solo i valori fuori dalle soglie (pressione, saturazione, febbre…)"],
  ["terapie", "Terapia non assunta", "Quando in Controllo terapie un farmaco risulta non preso"],
  ["eventi", "Eventi sanitari", "Apertura e chiusura di un evento sanitario"],
  ["non_alzati", "Ospite non alzato / non messo a letto", "Quando viene segnato il motivo per cui non è stato alzato o messo a letto"],
  ["pulizie_critiche", "Pulizie zone critiche saltate", "Pulizie non fatte nelle zone segnate come critiche"],
];

const Riga = ({ k, l, d, on, imp, onOn, onImp, extra }) => (
  <div className="flex items-center gap-3 py-3 flex-wrap" data-testid={`cons-auto-riga-${k}`}>
    <Switch checked={on} onCheckedChange={onOn} data-testid={`cons-auto-${k}`} />
    <div className="flex-1 min-w-[200px]"><div className="text-sm font-semibold">{l}</div><div className="text-xs text-muted-foreground">{d}</div></div>
    {extra}
    <label className={`inline-flex items-center gap-1.5 text-xs font-semibold ${on ? "text-red-700" : "text-slate-400"}`}>
      <input type="checkbox" disabled={!on} checked={imp} onChange={(e) => onImp(e.target.checked)} className="accent-red-600" data-testid={`cons-auto-imp-${k}`} /> Importante
    </label>
  </div>
);

export function ConsegneAutoCard() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const admin = ["admin", "super_admin"].includes(user?.role);
  const { data } = useQuery({ queryKey: ["consegne-auto"], queryFn: async () => (await api.get("/consegne/auto-impostazioni")).data });
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setF(data); }, [data]);
  if (!f) return <div className="gr-card p-5 text-sm text-muted-foreground">Caricamento…</div>;
  const setOn = (k, v) => setF((p) => ({ ...p, attive: { ...p.attive, [k]: v } }));
  const setImp = (k, v) => setF((p) => ({ ...p, importanti: v ? [...new Set([...p.importanti, k])] : p.importanti.filter((x) => x !== k) }));
  const num = (k, min, max) => <span className="inline-flex items-center gap-1 text-xs text-slate-600">dal <Input type="number" min={min} max={max} value={f[k]} onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))} disabled={!admin} className="h-8 w-16 text-center" data-testid={`cons-auto-${k}`} /> giorno</span>;
  const salva = async () => {
    setBusy(true);
    try { await api.put("/consegne/auto-impostazioni", f); await qc.invalidateQueries({ queryKey: ["consegne-auto"] }); toast.success("Impostazioni consegne salvate"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio"); } finally { setBusy(false); }
  };
  return (
    <div className="gr-card p-5" data-testid="cons-auto-card">
      <h3 className="font-head font-bold text-lg">Consegne automatiche</h3>
      <p className="text-sm text-muted-foreground">Scegli quali fatti, registrati dagli operatori, vengono scritti da soli nelle consegne del turno (mattina, pomeriggio o notte). «Importante» li mette in cima con il bordo rosso.</p>
      <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900 flex items-start gap-2" data-testid="cons-auto-nota"><Info size={14} className="mt-0.5 shrink-0" /> Le consegne automatiche partiranno con la nuova pagina Consegne: queste scelte verranno usate da lì.</div>
      <fieldset disabled={!admin} className="mt-2 divide-y">
        {VOCI.map(([k, l, d]) => <Riga key={k} k={k} l={l} d={d} on={!!f.attive[k]} imp={f.importanti.includes(k)} onOn={(v) => setOn(k, v)} onImp={(v) => setImp(k, v)}
          extra={k === "alvo" && f.attive.alvo ? num("alvo_giorni", 1, 10) : k === "non_mangia" && f.attive.non_mangia ? num("non_mangia_giorni", 1, 7) : null} />)}
        <div className="flex items-center gap-3 py-3"><Switch checked={!!f.commenti} onCheckedChange={(v) => setF((p) => ({ ...p, commenti: v }))} data-testid="cons-auto-commenti" /><div className="flex-1"><div className="text-sm font-semibold">Commenti degli operatori</div><div className="text-xs text-muted-foreground">Sotto una consegna automatica si può aggiungere una riga (es. «offerto yogurt, mangiato metà»)</div></div></div>
      </fieldset>
      {admin ? <div className="flex justify-end pt-2"><Button onClick={salva} disabled={busy} className="font-semibold" data-testid="cons-auto-salva"><Save size={15} className="mr-1" /> {busy ? "Salvataggio…" : "Salva"}</Button></div>
        : <p className="text-xs text-muted-foreground pt-2">Solo l'amministratore può modificare queste impostazioni.</p>}
    </div>
  );
}
